import React, { PureComponent, type RefObject } from "react";
import { type Root, createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import type { Store } from "@reduxjs/toolkit";
import type { PDFDocumentProxy } from "pdfjs-dist";
import type { EventBus, PDFViewer } from "pdfjs-dist/legacy/web/pdf_viewer.mjs";
import type { PDFViewerOptions } from "pdfjs-dist/types/web/pdf_viewer";
import styled from "styled-components";
import { debounce } from "ts-debounce";
import { scaledToViewport, viewportToScaled } from "../lib/coordinates";
import { findOrCreateContainerLayer } from "../lib/pdfjs-dom";
import type {
  IHighlight,
  LTWHP,
  Position,
  Scaled,
  ScaledPosition,
} from "../types";
import { HighlightLayer } from "./HighlightLayer";

const Container = styled.div`
  position: absolute;
  overflow: auto;
  width: 100%;
  height: 100%;
`;

export type ViewportHighlight<T> = { position: Position } & T;

interface State<T> {
  ghostHighlight: {
    position: ScaledPosition;
    content?: { text?: string; image?: string };
  } | null;
  isCollapsed: boolean;
  range: Range | null;
  tip: {
    highlight: ViewportHighlight<T>;
    callback: (highlight: ViewportHighlight<T>) => JSX.Element;
  } | null;
  tipPosition: Position | null;
  tipChildren: JSX.Element | null;
  isAreaSelectionInProgress: boolean;
  scrolledToHighlightId: string;
}

interface Props<T> {
  store: Store;
  highlightTransform: (
    highlight: ViewportHighlight<T>,
    index: number,
    viewportToScaled: (rect: LTWHP) => Scaled
  ) => JSX.Element;
  highlights: Array<T>;
  onScrollChange?: () => void;
  onDocumentReady?: () => void;
  pdfDocument: PDFDocumentProxy;
  pdfScaleValue: string;
  onSelectionFinished?: (
    position: ScaledPosition,
    content: { text?: string; image?: string },
    hideTipAndSelection: () => void,
    transformSelection: () => void
  ) => JSX.Element | null;
  enableAreaSelection?: (event: MouseEvent) => boolean;
  pdfViewerOptions?: PDFViewerOptions;
}

const EMPTY_ID = "empty-id";

export class PdfHighlighter<T extends IHighlight> extends PureComponent<
  Props<T>,
  State<T>
> {
  static defaultProps = {
    pdfScaleValue: "auto",
  };

  state: State<T> = {
    ghostHighlight: null,
    isCollapsed: true,
    range: null,
    scrolledToHighlightId: EMPTY_ID,
    isAreaSelectionInProgress: false,
    tip: null,
    tipPosition: null,
    tipChildren: null,
  };

  viewer!: PDFViewer;

  resizeObserver: ResizeObserver | null = null;
  containerNode?: HTMLDivElement | null = null;
  containerNodeRef: RefObject<HTMLDivElement>;
  highlightRoots: {
    [page: number]: { reactRoot: Root; container: Element };
  } = {};
  unsubscribe = (): void => undefined;

  constructor(props: Props<T>) {
    super(props);
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.debouncedScaleValue);
    }
    this.containerNodeRef = React.createRef();
  }

  componentDidMount() {
    this.init();
  }

  componentDidUpdate(prevProps: Props<T>) {
    if (prevProps.pdfDocument !== this.props.pdfDocument) {
      this.init();
      return;
    }
    if (prevProps.highlights !== this.props.highlights) {
      this.renderHighlightLayers();
    }
  }

  async init() {
    const { pdfDocument, pdfViewerOptions } = this.props;
    const pdfjs = await import("pdfjs-dist/web/pdf_viewer.mjs");

    const eventBus = new pdfjs.EventBus();
    const linkService = new pdfjs.PDFLinkService({
      eventBus,
      externalLinkTarget: 2,
    });

    if (!this.containerNodeRef.current) {
      throw new Error("containerNodeRef is not set");
    }

    this.viewer =
      this.viewer ||
      new pdfjs.PDFViewer({
        container: this.containerNodeRef.current,
        eventBus: eventBus,
        textLayerMode: 2,
        removePageBorders: true,
        linkService: linkService,
        ...pdfViewerOptions,
      });

    linkService.setDocument(pdfDocument);
    linkService.setViewer(this.viewer);
    this.viewer.setDocument(pdfDocument);

    this.attachRef(eventBus);
  }

  attachRef = (eventBus: EventBus) => {
    const { resizeObserver: observer } = this;
    this.containerNode = this.containerNodeRef.current;
    this.unsubscribe();

    if (this.containerNode) {
      const { ownerDocument: doc } = this.containerNode;
      eventBus.on("textlayerrendered", this.onTextLayerRendered);
      eventBus.on("pagesinit", this.onDocumentReady);
      doc.defaultView?.addEventListener("resize", this.debouncedScaleValue);
      this.viewer.container.addEventListener("scroll", this.debouncedOnScroll);
      if (observer) observer.observe(this.containerNode);

      this.unsubscribe = () => {
        eventBus.off("pagesinit", this.onDocumentReady);
        eventBus.off("textlayerrendered", this.onTextLayerRendered);
        this.viewer.container.removeEventListener(
          "scroll",
          this.debouncedOnScroll
        );
        doc.defaultView?.removeEventListener(
          "resize",
          this.debouncedScaleValue
        );
        if (observer) observer.disconnect();
      };
    }
  };

  componentWillUnmount() {
    this.unsubscribe();
  }

  onScroll = () => {
    this.renderHighlightLayers();
  };

  debouncedOnScroll: () => void = debounce(this.onScroll, 50);

  findOrCreateHighlightLayer(page: number): Element | null {
    const { textLayer } = this.viewer.getPageView(page - 1) || {};

    if (!textLayer) {
      return null;
    }

    return findOrCreateContainerLayer(
      textLayer.div,
      `PdfHighlighter__highlight-layer`,
      ".PdfHighlighter__highlight-layer"
    );
  }

  groupHighlightsByPage(highlights: Array<T>): {
    [pageNumber: string]: Array<T>;
  } {
    const { ghostHighlight } = this.state;

    const allHighlights = [...highlights, ghostHighlight].filter(
      Boolean
    ) as T[];

    const pageNumbers = new Set<number>();
    for (const highlight of allHighlights) {
      pageNumbers.add(highlight.position.pageNumber);
      for (const rect of highlight.position.rects) {
        if (rect.pageNumber) {
          pageNumbers.add(rect.pageNumber);
        }
      }
    }

    const groupedHighlights: Record<number, T[]> = {};

    for (const pageNumber of pageNumbers) {
      groupedHighlights[pageNumber] = groupedHighlights[pageNumber] || [];
      for (const highlight of allHighlights) {
        const pageSpecificHighlight = {
          ...highlight,
          position: {
            pageNumber,
            boundingRect: highlight.position.boundingRect,
            rects: [],
            usePdfCoordinates: highlight.position.usePdfCoordinates,
          } as ScaledPosition,
        };
        let anyRectsOnPage = false;
        for (const rect of highlight.position.rects) {
          if (
            pageNumber === (rect.pageNumber || highlight.position.pageNumber)
          ) {
            pageSpecificHighlight.position.rects.push(rect);
            anyRectsOnPage = true;
          }
        }
        if (anyRectsOnPage || pageNumber === highlight.position.pageNumber) {
          groupedHighlights[pageNumber].push(pageSpecificHighlight);
        }
      }
    }

    return groupedHighlights;
  }

  scaledPositionToViewport({
    pageNumber,
    boundingRect,
    rects,
    usePdfCoordinates,
  }: ScaledPosition): Position | null {
    const pageView = this.viewer.getPageView(pageNumber - 1);
    if (!pageView?.viewport) {
      return null;
    }
    const viewport = pageView.viewport;

    return {
      boundingRect: scaledToViewport(boundingRect, viewport, usePdfCoordinates),
      rects: (rects || []).map((rect) =>
        scaledToViewport(rect, viewport, usePdfCoordinates)
      ),
      pageNumber,
    };
  }

  viewportPositionToScaled({
    pageNumber,
    boundingRect,
    rects,
  }: Position): ScaledPosition | null {
    const pageView = this.viewer.getPageView(pageNumber - 1);
    if (!pageView?.viewport) {
      return null;
    }
    const viewport = pageView.viewport;

    return {
      boundingRect: viewportToScaled(boundingRect, viewport),
      rects: (rects || []).map((rect) => viewportToScaled(rect, viewport)),
      pageNumber,
    };
  }

  onTextLayerRendered = () => {
    this.renderHighlightLayers();
  };

  onDocumentReady = () => {
    this.handleScaleValue();
    this.renderHighlightLayers();
    this.props.onDocumentReady?.();
  };

  handleScaleValue = () => {
    if (!this.viewer) return;
    this.viewer.currentScaleValue = this.props.pdfScaleValue;
    this.renderHighlightLayers();
  };

  debouncedScaleValue: () => void = debounce(this.handleScaleValue, 500);

  render() {
    return (
      <Container
        ref={this.containerNodeRef}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="pdfViewer" />
      </Container>
    );
  }

  private renderHighlightLayers() {
    const { pdfDocument } = this.props;
    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      const highlightRoot = this.highlightRoots[pageNumber];
      if (highlightRoot?.container.isConnected) {
        this.renderHighlightLayer(highlightRoot.reactRoot, pageNumber);
      } else {
        const highlightLayer = this.findOrCreateHighlightLayer(pageNumber);
        if (!highlightLayer) {
          continue;
        }
        const reactRoot = createRoot(highlightLayer);
        this.highlightRoots[pageNumber] = {
          reactRoot,
          container: highlightLayer,
        };
        this.renderHighlightLayer(reactRoot, pageNumber);
      }
    }
  }

  private renderHighlightLayer(root: Root, pageNumber: number) {
    const { highlightTransform, highlights } = this.props;
    root.render(
      <Provider store={this.props.store}>
        <HighlightLayer
          highlightsByPage={this.groupHighlightsByPage(highlights)}
          pageNumber={pageNumber.toString()}
          highlightTransform={highlightTransform}
          scaledPositionToViewport={this.scaledPositionToViewport.bind(this)}
          viewer={this.viewer}
        />
      </Provider>
    );
  }
}
